/**
 * Palette builder: maps varnish-theme design tokens to MUI palette options for light/dark modes.
 * @see https://mui.com/material-ui/customization/palette/
 */
import varnishTokens, { type PaletteModes } from '@allenai/varnish-theme/mui';
import { createTheme } from '@mui/material';

export const buildPalette = (mode: PaletteModes) => {
    // augmentColor generates light/dark variants for custom palette entries (tertiary, default)
    const { augmentColor } = createTheme().palette;
    const palette = varnishTokens.palette[mode];
    return {
        mode,
        primary: {
            main: palette.accent.primary.value,
            contrastText: palette.elements.primary.contrast.value,
            hovered: {
                fill: palette.elements.primary.hovered.fill.value,
                stroke: palette.elements.primary.hovered.stroke.value,
            },
            stroke: palette.elements.primary.stroke.value,
            contrastReversed: palette.elements.primary['contrast-reversed'].value,
            text: palette.elements.primary.text.value,
        },
        secondary: {
            main: palette.accent.secondary.value,
            contrastText: palette.elements.secondary.contrast.value,
            hovered: {
                fill: palette.elements.secondary.hovered.fill.value,
                stroke: palette.elements.secondary.hovered.stroke.value,
            },
            stroke: palette.elements.secondary.stroke.value,
            contrastReversed: palette.elements.secondary['contrast-reversed'].value,
        },
        tertiary: {
            ...augmentColor({
                color: {
                    main: palette.accent.tertiary.value,
                    contrastText: palette.elements.tertiary.contrast.value,
                },
            }),
            hovered: {
                fill: palette.elements.tertiary.hovered.fill.value,
                stroke: palette.elements.tertiary.hovered.stroke.value,
            },
            stroke: palette.elements.tertiary.stroke.value,
            contrastReversed: palette.elements.tertiary['contrast-reversed'].value,
        },
        default: {
            ...augmentColor({
                color: {
                    main: palette.elements.default.fill.value,
                    contrastText: palette.elements.default.contrast.value,
                },
            }),
            hovered: {
                fill: palette.elements.default.hovered.fill.value,
                stroke: palette.elements.default.hovered.stroke.value,
            },
            stroke: palette.elements.default.stroke.value,
            contrastReversed: palette.elements.default['contrast-reversed'].value,
        },
        error: {
            main: palette.alert.error.value,
            contrastText: palette.elements.error.contrast.value,
            hovered: {
                fill: palette.elements.error.hovered.fill.value,
                stroke: palette.elements.error.hovered.stroke.value,
            },
            stroke: palette.elements.error.stroke.value,
            contrastReversed: palette.elements.error['contrast-reversed'].value,
        },
        warning: {
            main: palette.alert.warning.value,
            contrastText: palette.elements.warning.contrast.value,
            hovered: {
                fill: palette.elements.warning.hovered.fill.value,
                stroke: palette.elements.warning.hovered.stroke.value,
            },
            stroke: palette.elements.warning.stroke.value,
            contrastReversed: palette.elements.warning['contrast-reversed'].value,
        },
        info: {
            main: palette.alert.information.value,
            contrastText: palette.elements.information.contrast.value,
            hovered: {
                fill: palette.elements.information.hovered.fill.value,
                stroke: palette.elements.information.hovered.stroke.value,
            },
            stroke: palette.elements.information.stroke.value,
            contrastReversed: palette.elements.information['contrast-reversed'].value,
        },
        success: {
            main: palette.alert.confirmation.value,
            contrastText: palette.elements.confirmation.contrast.value,
            hovered: {
                fill: palette.elements.confirmation.hovered.fill.value,
                stroke: palette.elements.confirmation.hovered.stroke.value,
            },
            stroke: palette.elements.confirmation.stroke.value,
            contrastReversed: palette.elements.confirmation['contrast-reversed'].value,
        },
        grey: {
            50: varnishTokens.color['gray-10'].value,
            100: varnishTokens.color['gray-10'].value,
            200: varnishTokens.color['gray-30'].value,
            300: varnishTokens.color['gray-30'].value,
            400: varnishTokens.color['gray-50'].value,
            500: varnishTokens.color['gray-50'].value,
            600: varnishTokens.color['gray-50'].value,
            700: varnishTokens.color['gray-80'].value,
            800: varnishTokens.color['gray-80'].value,
            900: varnishTokens.color['gray-80'].value,
            A100: varnishTokens.color['gray-10'].value,
            A200: varnishTokens.color['gray-30'].value,
            A400: varnishTokens.color['gray-50'].value,
            A700: varnishTokens.color['gray-80'].value,
        },
        text: {
            primary: palette.text.primary.default.value,
            reversed: palette.text.primary.reversed.value,
            placeholder: palette.text.placeholder.value,
            placeholder55: palette.text['placeholder-55'].value,
            disabled: palette.text.disabled.value,
            error: palette.text.error.value,
        },
        divider: palette.stroke['opacity-20'].default.value,
        background: {
            default: palette.background.default.value,
            paper: palette.background.default.value,
            reversed: palette.background.reversed.value,
            opacity4: {
                default: palette.background['opacity-4'].default.value,
                reversed: palette.background['opacity-4'].reversed.value,
            },
            opacity10: {
                default: palette.background['opacity-10'].default.value,
                reversed: palette.background['opacity-10'].reversed.value,
            },
            opacity20: {
                default: palette.background['opacity-20'].default.value,
                reversed: palette.background['opacity-20'].reversed.value,
            },
            opacity30: {
                default: palette.background['opacity-30'].default.value,
                reversed: palette.background['opacity-30'].reversed.value,
            },
            opacity40: {
                default: palette.background['opacity-40'].default.value,
                reversed: palette.background['opacity-40'].reversed.value,
            },
            opacity50: {
                default: palette.background['opacity-50'].default.value,
                reversed: palette.background['opacity-50'].reversed.value,
            },
            opacity60: {
                default: palette.background['opacity-60'].default.value,
                reversed: palette.background['opacity-60'].reversed.value,
            },
            opacity70: {
                default: palette.background['opacity-70'].default.value,
                reversed: palette.background['opacity-70'].reversed.value,
            },
            opacity80: {
                default: palette.background['opacity-80'].default.value,
                reversed: palette.background['opacity-80'].reversed.value,
            },
            opacity90: {
                default: palette.background['opacity-90'].default.value,
                reversed: palette.background['opacity-90'].reversed.value,
            },
        },
        common: {
            white: varnishTokens.color.white.value,
            black: varnishTokens.color.black.value,
        },
        stroke: {
            opacity4: {
                default: palette.stroke['opacity-4'].default.value,
                reversed: palette.stroke['opacity-4'].reversed.value,
            },
            opacity10: {
                default: palette.stroke['opacity-10'].default.value,
                reversed: palette.stroke['opacity-10'].reversed.value,
            },
            opacity20: {
                default: palette.stroke['opacity-20'].default.value,
                reversed: palette.stroke['opacity-20'].reversed.value,
            },
            opacity30: {
                default: palette.stroke['opacity-30'].default.value,
                reversed: palette.stroke['opacity-30'].reversed.value,
            },
            opacity40: {
                default: palette.stroke['opacity-40'].default.value,
                reversed: palette.stroke['opacity-40'].reversed.value,
            },
            opacity50: {
                default: palette.stroke['opacity-50'].default.value,
                reversed: palette.stroke['opacity-50'].reversed.value,
            },
            opacity60: {
                default: palette.stroke['opacity-60'].default.value,
                reversed: palette.stroke['opacity-60'].reversed.value,
            },
            opacity70: {
                default: palette.stroke['opacity-70'].default.value,
                reversed: palette.stroke['opacity-70'].reversed.value,
            },
            opacity80: {
                default: palette.stroke['opacity-80'].default.value,
                reversed: palette.stroke['opacity-80'].reversed.value,
            },
            opacity90: {
                default: palette.stroke['opacity-90'].default.value,
                reversed: palette.stroke['opacity-90'].reversed.value,
            },
            opacity100: {
                default: palette.stroke['opacity-100'].default.value,
                reversed: palette.stroke['opacity-100'].reversed.value,
            },
            disabled: {
                default: palette.stroke.disabled.default.value,
            },
        },
        icon: {
            default: palette.icon.default.value,
            reversed: palette.icon.reversed.value,
            disabled: palette.icon.disabled.value,
            error: palette.icon.error.value,
            opacity50: palette.icon['opacity-50'].value,
            on: palette.icon.on.value,
            opacity30: palette.icon['opacity-30'].value,
            // opacity-55 only exists in light mode tokens; cast needed because TS can't narrow the union type.
            // TODO: add opacity-55 to dark mode in varnish-theme so this conditional spread can be removed.
            ...('opacity-55' in palette.icon && {
                opacity55: (palette.icon as Record<string, { value: string }>)['opacity-55'].value,
            }),
        },
        links: {
            default: palette.links.default.value,
            reversed: palette.links.reversed.value,
            hovered: {
                default: palette.links.hovered.default.value,
                reversed: palette.links.hovered.reversed.value,
            },
        },
        elements: {
            disabled: {
                fill: palette.elements.disabled.fill.value,
                hovered: {
                    fill: palette.elements.disabled.hovered.fill.value,
                    stroke: palette.elements.disabled.hovered.stroke.value,
                },
                contrast: palette.elements.disabled.contrast.value,
                stroke: palette.elements.disabled.stroke.value,
                contrastReversed: palette.elements.disabled['contrast-reversed'].value,
            },
            default: {
                fill: palette.elements.default.fill.value,
                hovered: {
                    fill: palette.elements.default.hovered.fill.value,
                    stroke: palette.elements.default.hovered.stroke.value,
                },
                contrast: palette.elements.default.contrast.value,
                stroke: palette.elements.default.stroke.value,
                contrastReversed: palette.elements.default['contrast-reversed'].value,
            },
            faded: {
                fill: palette.elements.faded.fill.value,
                hovered: {
                    fill: palette.elements.faded.hovered.fill.value,
                    stroke: palette.elements.faded.hovered.stroke.value,
                },
                contrast: palette.elements.faded.contrast.value,
                stroke: palette.elements.faded.stroke.value,
                contrastReversed: palette.elements.faded['contrast-reversed'].value,
            },
            reversed: {
                fill: palette.elements.reversed.fill.value,
                hovered: {
                    fill: palette.elements.reversed.hovered.fill.value,
                    stroke: palette.elements.reversed.hovered.stroke.value,
                },
                contrast: palette.elements.reversed.contrast.value,
                stroke: palette.elements.reversed.stroke.value,
                contrastReversed: palette.elements.reversed['contrast-reversed'].value,
            },
            overlay: {
                background: palette.elements.overlay.background.value,
                header: palette.elements.overlay.header.value,
                footer: palette.elements.overlay.footer.value,
                contentContrastMed: palette.elements.overlay['content-contrast-med'].value,
                contentContrastHigh: palette.elements.overlay['content-contrast-high'].value,
                contentContrastLow: palette.elements.overlay['content-contrast-low'].value,
                contentContrastHighGradient:
                    palette.elements.overlay['content-contrast-high-gradient'].value,
            },
        },
        form: {
            checkbox: {
                stroke: palette.elements.overrides.form.checkbox.stroke.value,
                fill: palette.elements.overrides.form.checkbox.fill.value,
                hovered: {
                    fill: palette.elements.overrides.form.checkbox.hovered.fill.value,
                    stroke: palette.elements.overrides.form.checkbox.hovered.stroke.value,
                },
                error: {
                    fill: palette.elements.overrides.form.checkbox.error.fill.value,
                    stroke: palette.elements.overrides.form.checkbox.error.stroke.value,
                    contrast: palette.elements.overrides.form.checkbox.error.contrast.value,
                },
                checked: {
                    fill: palette.elements.overrides.form.checkbox.checked.fill.value,
                    contrast: palette.elements.overrides.form.checkbox.checked.contrast.value,
                },
                disabled: {
                    fill: palette.elements.overrides.form.checkbox.disabled.fill.value,
                    stroke: palette.elements.overrides.form.checkbox.disabled.stroke.value,
                    contrast: palette.elements.overrides.form.checkbox.disabled.contrast.value,
                },
            },
            input: {
                stroke: palette.elements.overrides.form.input.stroke.value,
                fill: palette.elements.overrides.form.input.fill.value,
                contrast: palette.elements.overrides.form.input.contrast.value,
                hovered: {
                    fill: palette.elements.overrides.form.input.hovered.fill.value,
                    stroke: palette.elements.overrides.form.input.hovered.stroke.value,
                },
                error: {
                    fill: palette.elements.overrides.form.input.error.fill.value,
                    stroke: palette.elements.overrides.form.input.error.stroke.value,
                    contrast: palette.elements.overrides.form.input.error.contrast.value,
                },
                disabled: {
                    fill: palette.elements.overrides.form.input.disabled.fill.value,
                    stroke: palette.elements.overrides.form.input.disabled.stroke.value,
                    contrast: palette.elements.overrides.form.input.disabled.contrast.value,
                },
            },
            radio: {
                stroke: palette.elements.overrides.form.radio.stroke.value,
                fill: palette.elements.overrides.form.radio.fill.value,
                hovered: {
                    fill: palette.elements.overrides.form.radio.hovered.fill.value,
                    stroke: palette.elements.overrides.form.radio.hovered.stroke.value,
                },
                error: {
                    fill: palette.elements.overrides.form.radio.error.fill.value,
                    stroke: palette.elements.overrides.form.radio.error.stroke.value,
                    contrast: palette.elements.overrides.form.radio.error.contrast.value,
                },
                checked: {
                    fill: palette.elements.overrides.form.radio.checked.fill.value,
                    contrast: palette.elements.overrides.form.radio.checked.contrast.value,
                },
                disabled: {
                    fill: palette.elements.overrides.form.radio.disabled.fill.value,
                    stroke: palette.elements.overrides.form.radio.disabled.stroke.value,
                    contrast: palette.elements.overrides.form.radio.disabled.contrast.value,
                },
            },
            switch: {
                stroke: palette.elements.overrides.form.switch.stroke.value,
                fill: palette.elements.overrides.form.switch.fill.value,
                hovered: {
                    fill: palette.elements.overrides.form.switch.hovered.fill.value,
                    stroke: palette.elements.overrides.form.switch.hovered.stroke.value,
                },
                error: {
                    fill: palette.elements.overrides.form.switch.error.fill.value,
                    stroke: palette.elements.overrides.form.switch.error.stroke.value,
                    contrast: palette.elements.overrides.form.switch.error.contrast.value,
                },
                checked: {
                    fill: palette.elements.overrides.form.switch.checked.fill.value,
                    contrast: palette.elements.overrides.form.switch.checked.contrast.value,
                },
                disabled: {
                    fill: palette.elements.overrides.form.switch.disabled.fill.value,
                    stroke: palette.elements.overrides.form.switch.disabled.stroke.value,
                    contrast: palette.elements.overrides.form.switch.disabled.contrast.value,
                },
            },
            slider: {
                stroke: palette.elements.overrides.form.slider.stroke.value,
                fill: palette.elements.overrides.form.slider.fill.value,
                hover: {
                    fill: palette.elements.overrides.form.slider.hover.fill.value,
                    stroke: palette.elements.overrides.form.slider.hover.stroke.value,
                },
            },
        },
        button: {
            text: {
                hovered: {
                    fill: palette.elements.overrides.button.text.hovered.fill.value,
                },
            },
        },
    };
};
